/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { BrowserRouter, Route, Switch, Redirect } from 'react-router-dom';
import { MainLayout } from '@eriksdigital/atomic-ui/components';
import { breakpointObj } from '@eriksdigital/atomic-ui/styles';
import {
  selectBasenamePath,
  selectLanguage,
} from './features/erpSystem/slice/selectors';
import { Footer, Header } from 'app/components/organisms';
import { SelectorPage } from 'app/pages/SelectorPage/Loadable';
import { selectModalShow } from './features/modal/slice/selectors';
import { Modal, MobileSupport, Stepper } from 'app/components/molecules';
import { modalActions } from 'app/features/modal/slice';
import { configuratorSteps } from 'utils/configuratorSteps';
import { selectStep } from './features/step/slice/selectors';
import { ServicePage } from './pages/ServicePage/Loadable';
import { OverviewPage } from './pages/OverviewPage/Loadable';
import { SuccessPage } from './pages/SuccessPage/Loadable';
import { salesOrganizationActions } from './features/salesOrganisation/slice';
import { ErrorNotification } from './components/atoms/ErrorNotification';
import { selectErrorNotifyShow } from './features/errorNotifications/slice/selectors';
import { errorNotifyActions } from './features/errorNotifications/slice';

const fetchTranslations = async ({ language = 'en' }) => {
  try {
    if (process.env.REACT_APP_USE_MOCK_TRANSLATIONS === 'YES') {
      const module = await import(`./../mocks/mockLocales/${language}`);
      return await module.default;
    } else {
      const module = await import(`../../locales/${language}`);
      return await module.default;
    }
  } catch (e) {
    throw e;
  }
};

const App = () => {
  const dispatch = useDispatch();
  const language = useSelector(selectLanguage);
  const basenamePath = useSelector(selectBasenamePath);
  const [messages, setMessages] = useState(fetchTranslations({ language }));
  const showModal = useSelector(selectModalShow);
  const currentStep = useSelector(selectStep);
  const { openModal } = modalActions;
  const { getSalesOrganization } = salesOrganizationActions;
  const showErrorNotify = useSelector(selectErrorNotifyShow);
  const { openErrorNotification } = errorNotifyActions;

  useEffect(() => {
    fetchTranslations({ language }).then(translation =>
      setMessages(translation),
    );
  }, [language]);

  useEffect(() => {
    dispatch(getSalesOrganization());
  }, [dispatch, getSalesOrganization]);

  useEffect(() => {
    if (window.innerWidth < breakpointObj.tabletPortrait) {
      // To Remove modal after app is optimized for mobile devices
      dispatch(
        openModal({
          show: true,
          content: {
            type: 'TextContent',
            textElement: <MobileSupport />,
          },
        }),
      );
    }
  }, [dispatch, openModal, language]);

  useEffect(() => {
    dispatch(openErrorNotification({ show: false }));
  }, [dispatch, openErrorNotification]);

  const renderPage = (id: number) => {
    if (id === 5) {
      return ServicePage;
    }

    if (id === 6) {
      return OverviewPage;
    }

    return SelectorPage;
  };

  return (
    // @ts-ignore
    <IntlProvider locale={language} messages={messages}>
      <BrowserRouter>
        <Helmet
          titleTemplate="%s"
          defaultTitle="Valve Configurator"
          htmlAttributes={{ lang: language }}
        >
          <meta name="description" content="A Valve Configurator application" />
        </Helmet>
        <Header />
        <MainLayout
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <Stepper />
          {showErrorNotify && (
            <ErrorNotification
              label="Our server encountered an error while sending the configuration to SAP. Please try again. If the problem persists, please log an incident in {ServiceNow}."
              translationId="SAP.error.notify"
            />
          )}
          <Switch>
            {configuratorSteps.map(route => {
              return (
                <Route
                  exact
                  path={`${basenamePath}/${route.name}`}
                  component={renderPage(route.id)}
                  key={route.id}
                />
              );
            })}
            <Route path={`${basenamePath}/success`} component={SuccessPage} />
          </Switch>
          {currentStep === 1 && (
            <Redirect
              path="*"
              to={{
                pathname: `${basenamePath}/valve`,
                search: window.location.search,
              }}
            />
          )}
          {showModal && <Modal />}
        </MainLayout>
        <Footer />
      </BrowserRouter>
    </IntlProvider>
  );
};

export default App;
